import NotificationsService from '../../services/notifications';

const state = {
  activeCount: 0,
  activeNotifications: [],
  historyNotifications: [],
  lastSeenAt: null,
  loading: false,
  error: null,
};

const getters = {
  activeCount: (state) => state.activeCount,
  activeNotifications: (state) => state.activeNotifications,
  historyNotifications: (state) => state.historyNotifications,
  lastSeenAt: (state) => state.lastSeenAt,
  loading: (state) => state.loading,
  error: (state) => state.error,
};

const mutations = {
  SET_ACTIVE_COUNT(state, count) {
    state.activeCount = count;
  },
  SET_ACTIVE_NOTIFICATIONS(state, notifications) {
    state.activeNotifications = notifications;
  },
  SET_HISTORY_NOTIFICATIONS(state, notifications) {
    state.historyNotifications = notifications;
  },
  SET_LAST_SEEN_AT(state, isoString) {
    state.lastSeenAt = isoString;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  DISMISS_NOTIFICATION(state, notificationId) {
    state.activeNotifications = state.activeNotifications.filter(n => n.id !== notificationId);
    state.activeCount = state.activeNotifications.length;
    const historyNotif = state.historyNotifications.find(n => n.id === notificationId);
    if (historyNotif) {
      historyNotif.isDismissed = true;
    }
  },
};

const actions = {
  initLastSeenAt({ commit }) {
    try {
      const stored = localStorage.getItem('notifications_last_seen_at');
      commit('SET_LAST_SEEN_AT', stored || null);
    } catch (e) {
      commit('SET_LAST_SEEN_AT', null);
    }
  },

  setLastSeenAtNow({ commit }) {
    const now = new Date().toISOString();
    commit('SET_LAST_SEEN_AT', now);
    try {
      localStorage.setItem('notifications_last_seen_at', now);
    } catch (e) {
      // ignore
    }
    return now;
  },

  setLastSeenAt({ commit }, value) {
    if (!value) return null;
    const normalized = typeof value === 'number' ? new Date(value).toISOString() : String(value);
    commit('SET_LAST_SEEN_AT', normalized);
    try {
      localStorage.setItem('notifications_last_seen_at', normalized);
    } catch (e) {
      // ignore
    }
    return normalized;
  },

  async fetchActiveNotifications({ commit }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      // UX: Treat backend "unread" endpoint as ACTIVE list for now
      const notifications = await (NotificationsService.getActiveNotificationsForMe
        ? NotificationsService.getActiveNotificationsForMe()
        : NotificationsService.getUnreadNotifications());
      const notificationsArray = Array.isArray(notifications)
        ? notifications
        : Array.isArray(notifications?.data)
          ? notifications.data
          : Array.isArray(notifications?.notifications)
            ? notifications.notifications
            : [];
      const active = notificationsArray.filter((n) => {
        const isDismissed = n?.isDismissed === true || n?.isDismissed === 'true' || n?.isDismissed === 1;
        const hasIsActive = typeof n?.isActive !== 'undefined' && n !== null;
        const isActive = !hasIsActive
          ? true
          : n?.isActive === true || n?.isActive === 'true' || n?.isActive === 1;
        return isActive && !isDismissed;
      });
      commit('SET_ACTIVE_NOTIFICATIONS', active);
      commit('SET_ACTIVE_COUNT', active.length);
      return active;
    } catch (error) {
      console.error('Failed to fetch active notifications:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch notifications');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchHistoryNotifications({ commit }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const notifications = await NotificationsService.getHistoryNotifications();
      const notificationsArray = Array.isArray(notifications)
        ? notifications
        : Array.isArray(notifications?.data)
          ? notifications.data
          : Array.isArray(notifications?.notifications)
            ? notifications.notifications
            : [];
      commit('SET_HISTORY_NOTIFICATIONS', notificationsArray);
      return notificationsArray;
    } catch (error) {
      console.error('Failed to fetch history notifications:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch history');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async dismissNotification({ commit }, notificationId) {
    try {
      await NotificationsService.dismissNotification(notificationId);
      commit('DISMISS_NOTIFICATION', notificationId);
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
      throw error;
    }
  },

  async markAllSeen({ dispatch }) {
    try {
      const response = await NotificationsService.markAllSeen();
      const backendLastSeenAt = response?.lastSeenAt || response?.seenAt || response?.timestamp || null;
      if (backendLastSeenAt) {
        dispatch('setLastSeenAt', backendLastSeenAt);
      } else {
        dispatch('setLastSeenAtNow');
      }
      return response;
    } catch (error) {
      console.error('Failed to mark all as seen:', error);
      // still update local lastSeenAt so "new" highlight clears as expected
      dispatch('setLastSeenAtNow');
      throw error;
    }
  },

  clearError({ commit }) {
    commit('SET_ERROR', null);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
